import { useMemo, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useAuth } from '@/context/AuthContext';
import { useCommissions } from '@/hooks/useCommissions';
import {
  setCommissionPaid,
  deleteCommission,
  markSalespersonCommissionsPaid,
  type Commission,
} from '@/services/commissions';
import CommissionModal from './components/CommissionModal';
import CommissionPaymentsModal from './components/CommissionPaymentsModal';
import CommissionReport from './components/CommissionReport';
import Modal from '@/components/base/Modal';

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

interface Group {
  key: string;
  salespersonId: string;
  name: string;
  commissions: Commission[];
  pendingTotal: number;
  paidTotal: number;
  totalAmount: number;
}

export default function CommissionsPage() {
  const { profile } = useAuth();
  const isAgent = profile?.role === 'sales_agent';
  const { data, loading, error, refetch } = useCommissions();
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid'>('all');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Commission | null>(null);
  const [togglingId, setTogglingId] = useState('');
  const [deleting, setDeleting] = useState<Commission | null>(null);
  const [deletingNow, setDeletingNow] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [paymentsFor, setPaymentsFor] = useState<Commission | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [payAllFor, setPayAllFor] = useState<Group | null>(null);
  const [payingAll, setPayingAll] = useState(false);
  const [payAllError, setPayAllError] = useState('');

  const monthFiltered = data.filter((c) => {
    if (!month) return true;
    return (c.createdAt || '').slice(0, 7) === month;
  });

  const totalPending = monthFiltered.reduce((s, c) => s + c.pendingAmount, 0);
  const totalPaid = monthFiltered.reduce((s, c) => s + c.paidAmount, 0);

  const filtered = monthFiltered.filter((c) => {
    if (statusFilter === 'pending' && c.isPaid) return false;
    if (statusFilter === 'paid' && !c.isPaid) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return c.salespersonName.toLowerCase().includes(q) || c.saleNumber.toLowerCase().includes(q);
  });

  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, Group>();
    filtered.forEach((c) => {
      const key = c.salespersonId || '__none__';
      const g =
        map.get(key) ||
        ({
          key,
          salespersonId: c.salespersonId,
          name: c.salespersonName || 'Sin vendedor',
          commissions: [],
          pendingTotal: 0,
          paidTotal: 0,
          totalAmount: 0,
        } as Group);
      g.commissions.push(c);
      g.pendingTotal += c.pendingAmount;
      g.paidTotal += c.paidAmount;
      g.totalAmount += c.commissionAmount;
      map.set(key, g);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered]);

  function toggleGroup(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleToggle(id: string, isPaid: boolean) {
    setTogglingId(id);
    await setCommissionPaid(id, !isPaid);
    setTogglingId('');
    refetch();
  }

  async function handleDelete() {
    if (!deleting) return;
    setDeletingNow(true);
    setDeleteError('');
    const { error } = await deleteCommission(deleting.id);
    setDeletingNow(false);
    if (error) {
      setDeleteError(error);
      return;
    }
    setDeleting(null);
    refetch();
  }

  async function handlePayAll() {
    if (!payAllFor) return;
    setPayingAll(true);
    setPayAllError('');
    const pendingIds = payAllFor.commissions
      .filter((c) => c.pendingAmount > 0)
      .map((c) => c.id);
    const { error } = await markSalespersonCommissionsPaid(payAllFor.salespersonId, pendingIds);
    setPayingAll(false);
    if (error) {
      setPayAllError(error);
      return;
    }
    setPayAllFor(null);
    refetch();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Comisiones</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} comisiones registradas</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          {!isAgent && (
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nueva Comisión
            </button>
          )}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Pendientes</p>
            <p className="text-xl font-bold text-amber-600 mt-1">${totalPending.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Pagadas</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">${totalPaid.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">${(totalPending + totalPaid).toLocaleString()}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            {(['all', 'pending', 'paid'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  statusFilter === s
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {s === 'all' ? 'Todas' : s === 'pending' ? 'Pendientes' : 'Pagadas'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <div className="relative">
              <i className="ri-calendar-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
            {month && (
              <button
                onClick={() => setMonth('')}
                className="text-xs px-3 py-2 rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line mr-1"></i>Limpiar
              </button>
            )}
            <div className="relative flex-1 max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="Buscar por vendedor o venta..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
          </div>
        </div>

        {/* Grouped list by salesperson */}
        {loading ? (
          <div className="bg-white rounded-xl border border-background-200 py-14 text-center text-foreground-400">
            <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando comisiones...
          </div>
        ) : error ? null : groups.length === 0 ? (
          <div className="bg-white rounded-xl border border-background-200 text-center py-12 text-foreground-500">
            <i className="ri-percent-line text-3xl block mb-2"></i>
            <p className="text-sm">
              {data.length === 0
                ? 'Aún no hay comisiones registradas.'
                : 'No se encontraron comisiones con ese filtro.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const isOpen = expanded.has(g.key);
              return (
                <div key={g.key} className="bg-white rounded-xl border border-background-200 overflow-hidden">
                  {/* Group header */}
                  <div className="flex items-center gap-3 px-4 py-3.5 flex-wrap">
                    <button
                      onClick={() => toggleGroup(g.key)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer group"
                    >
                      <span className="w-7 h-7 rounded-full bg-accent-50 flex items-center justify-center flex-shrink-0">
                        <i
                          className={`ri-arrow-down-s-line text-accent-600 text-lg transition-transform ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </span>
                      <span className="w-9 h-9 rounded-full bg-accent-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-3-line text-accent-600"></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block font-semibold text-foreground-950 truncate">{g.name}</span>
                        <span className="block text-xs text-foreground-500">
                          {g.commissions.length} comisión{g.commissions.length === 1 ? '' : 'es'}
                        </span>
                      </span>
                    </button>

                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-right">
                        <p className="text-xs text-foreground-500">Pendiente</p>
                        <p className="font-bold text-amber-600 whitespace-nowrap">
                          {g.pendingTotal > 0 ? `$${g.pendingTotal.toLocaleString()}` : '—'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-foreground-500">Pagado</p>
                        <p className="font-bold text-emerald-600 whitespace-nowrap">
                          {g.paidTotal > 0 ? `$${g.paidTotal.toLocaleString()}` : '—'}
                        </p>
                      </div>
                      {!isAgent && g.pendingTotal > 0 && (
                        <button
                          onClick={() => {
                            setPayAllFor(g);
                            setPayAllError('');
                          }}
                          className="text-xs px-3 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
                        >
                          <i className="ri-bank-card-line mr-1.5"></i>Pagar todo
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div className="border-t border-background-100 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-background-100 text-left bg-background-50">
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500">Venta</th>
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Monto venta</th>
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">%</th>
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Comisión</th>
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Pagado</th>
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Por pagar</th>
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">Estado</th>
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-28 text-center">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.commissions.map((c) => (
                            <tr key={c.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                              <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">
                                <span className="font-medium text-foreground-900">{c.saleNumber || '—'}</span>
                                {c.createdAt && (
                                  <span className="block text-xs text-foreground-400">{formatDate(c.createdAt)}</span>
                                )}
                              </td>
                              <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">${c.saleAmount.toLocaleString()}</td>
                              <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">{c.commissionRate}%</td>
                              <td className="px-5 py-3 text-right font-semibold text-foreground-950 whitespace-nowrap">${c.commissionAmount.toLocaleString()}</td>
                              <td className="px-5 py-3 text-right text-emerald-600 whitespace-nowrap">{c.paidAmount > 0 ? `$${c.paidAmount.toLocaleString()}` : '—'}</td>
                              <td className="px-5 py-3 text-right text-amber-600 whitespace-nowrap">{c.pendingAmount > 0 ? `$${c.pendingAmount.toLocaleString()}` : '—'}</td>
                              <td className="px-5 py-3 text-center">
                                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${c.isPaid ? 'text-emerald-700 bg-emerald-50' : c.paidAmount > 0 ? 'text-cyan-700 bg-cyan-50' : 'text-amber-700 bg-amber-50'}`}>
                                  {c.isPaid ? 'Pagada' : c.paidAmount > 0 ? 'Parcial' : 'Pendiente'}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-center">
                                {!isAgent ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => setPaymentsFor(c)}
                                      className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                                      title="Abonos / pagos"
                                    >
                                      <i className="ri-cash-line text-foreground-500 text-lg"></i>
                                    </button>
                                    <button
                                      onClick={() => setEditing(c)}
                                      className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                                      title="Editar"
                                    >
                                      <i className="ri-pencil-line text-foreground-500"></i>
                                    </button>
                                    <button
                                      onClick={() => handleToggle(c.id, c.isPaid)}
                                      disabled={togglingId === c.id}
                                      className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                                      title={c.isPaid ? 'Marcar pendiente' : 'Marcar pagada'}
                                    >
                                      <i className={`${c.isPaid ? 'ri-checkbox-circle-fill text-emerald-600' : 'ri-checkbox-blank-circle-line text-foreground-400'} text-lg`}></i>
                                    </button>
                                    <button
                                      onClick={() => {
                                        setDeleting(c);
                                        setDeleteError('');
                                      }}
                                      className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                                      title="Eliminar"
                                    >
                                      <i className="ri-delete-bin-line text-red-500"></i>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-foreground-400">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <CommissionReport commissions={data} />
      </div>

      {(showNew || editing) && (
        <CommissionModal
          commission={editing}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
          }}
          onSaved={refetch}
        />
      )}

      {paymentsFor && (
        <CommissionPaymentsModal
          commission={paymentsFor}
          onClose={() => setPaymentsFor(null)}
          onSaved={refetch}
        />
      )}

      {deleting && (
        <Modal
          title="Eliminar comisión"
          subtitle={`Estás a punto de eliminar la comisión de "${deleting.salespersonName || '—'}" por ${deleting.saleNumber || '—'}.`}
          onClose={() => {
            setDeleting(null);
            setDeleteError('');
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setDeleting(null);
                  setDeleteError('');
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deletingNow}
                className="px-4 py-2 text-sm font-semibold bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deletingNow ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Eliminando...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line mr-1.5"></i>Eliminar
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {deleteError && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{deleteError}</span>
              </div>
            )}
            <p className="text-sm text-foreground-600">
              Esta acción no se puede deshacer. La comisión se eliminará de forma permanente.
            </p>
          </div>
        </Modal>
      )}

      {payAllFor && (
        <Modal
          title="Pagar todas las comisiones"
          subtitle={`Vendedor: ${payAllFor.name}`}
          onClose={() => {
            setPayAllFor(null);
            setPayAllError('');
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setPayAllFor(null);
                  setPayAllError('');
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handlePayAll}
                disabled={payingAll}
                className="px-4 py-2 text-sm font-semibold bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {payingAll ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Pagando...
                  </>
                ) : (
                  <>
                    <i className="ri-bank-card-line mr-1.5"></i>Pagar ${payAllFor.pendingTotal.toLocaleString()}
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {payAllError && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{payAllError}</span>
              </div>
            )}
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
              <p className="text-sm text-foreground-700">
                Vas a marcar como pagadas <strong>{payAllFor.commissions.filter((c) => c.pendingAmount > 0).length}</strong> comisión(es) pendientes de{' '}
                <strong>{payAllFor.name}</strong> por un total de{' '}
                <strong className="text-emerald-700">${payAllFor.pendingTotal.toLocaleString()}</strong>.
              </p>
            </div>
            <p className="text-sm text-foreground-600">
              Esta acción marca como pagadas todas las comisiones pendientes de este vendedor de una sola vez.
              Si necesitás liquidar una comisión específica, usá el desglose y pagá individualmente.
            </p>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}